import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useUser } from '../context/UserContext';
import Select from 'react-select';
import { CSVLink } from 'react-csv';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import 'react-datepicker/dist/react-datepicker.css';

const EmployeeAttendance = () => {
  const { selectedCenter } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [academyOptions, setAcademyOptions] = useState([]);
  const [selectedAcademy, setSelectedAcademy] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const todayStr = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const token = localStorage.getItem('auth-token');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [academiesLoaded, setAcademiesLoaded] = useState(false);

  const headers = useMemo(() => ([
    { label: "Center", key: "centerName" },
    { label: "Employee Name", key: "employeeName" },
    { label: "Department", key: "department" },
    { label: "Role", key: "role" },
    { label: "Employee Phone", key: "employeePhone" },
    { label: "Check-in Date", key: "checkinDate" },
    { label: "Check-in Time", key: "checkinTime" },
    { label: "Check-out Time", key: "checkoutTime" },
    { label: "Working Hours", key: "workingHours" },
    { label: "Remark", key: "reason" },
    { label: "Status", key: "status" },
  ]), []);

  const statusOptions = [
    { value: "Success", label: "Success" },
    { value: "Failure", label: "Failure" },
  ];

  // Extract unique departments and roles from attendance data
  const extractFilterOptions = (data) => {
    const departments = new Set();
    const roles = new Set();
    const employees = new Set();
    const academies = new Set();

    data.forEach(item => {
      if (item.departmentName) {
        departments.add(item.departmentName);
      }
      if (item.roleName) {
        roles.add(item.roleName);
      }
      if (item.academyName && item.academyId) {
        academies.add(JSON.stringify({
          value: item.academyId.toString(),
          label: item.academyName
        }));
      }
      if (item.name && item.empId) {
        employees.add(JSON.stringify({
          value: item.empId.toString(),
          label: item.name,
          academy: item.academyId?.toString(),
          contact: item.mobileNumber,
          department: item.departmentName,
          role: item.roleName
        }));
      }
    });

    // Convert sets to arrays and format for dropdowns
    const departmentOptions = Array.from(departments).map(dept => ({
      value: dept,
      label: dept
    }));

    const roleOptions = Array.from(roles).map(role => ({
      value: role,
      label: role
    }));

    const academyOptions = Array.from(academies).map(academy => JSON.parse(academy));
    const employeeOptions = Array.from(employees).map(emp => JSON.parse(emp));

    return { departmentOptions, roleOptions, academyOptions, employeeOptions };
  };

  // Fetch academies from API
  const fetchAcademies = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_ACADEMIES, {
        method: 'GET',
        headers: {
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const academies = data.data.map(academy => ({
          value: academy.id.toString(),
          label: academy.name
        }));
        setAcademyOptions(academies);
        return academies;
      }
      return [];
    } catch (error) {
      console.error('Error fetching academies:', error);
      return [];
    }
  };

  // Fetch all data in one go
  const fetchAllData = async () => {
    setLoading(true);
    setError('');
    try {
      // Build query parameters for initial load - get all data
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const apiUrl = `${process.env.REACT_APP_API_EMPLOYEE_ATTENDANCE}?${params.toString()}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data && result.data.items) {
        const { departmentOptions, roleOptions, academyOptions, employeeOptions } = extractFilterOptions(result.data.items);
        setDepartmentOptions(departmentOptions);
        setRoleOptions(roleOptions);
        setAcademyOptions(academyOptions);
        setEmployeeOptions(employeeOptions);

        if (selectedCenter && academyOptions.length > 0) {
          const matchedAcademy = academyOptions.find(academy => academy.value === String(selectedCenter));
          if (matchedAcademy) {
            setSelectedAcademy(matchedAcademy);
          } else if (academyOptions.length > 0) {
            setSelectedAcademy(academyOptions[0]);
          }
        } else if (academyOptions.length > 0) {
          setSelectedAcademy(academyOptions[0]);
        }

        const formatTime = (timeStr) => {
          if (!timeStr) return '-';
          const [hour, minute, second] = timeStr.split(':');
          const d = new Date();
          d.setHours(Number(hour || 0), Number(minute || 0), Number(second || 0));
          return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
        };

        const calculateWorkingHours = (checkInTime, checkOutTime) => {
          if (!checkInTime || !checkOutTime) return '-';
          
          const [inHour, inMinute, inSecond] = checkInTime.split(':').map(Number);
          const [outHour, outMinute, outSecond] = checkOutTime.split(':').map(Number);
          
          const checkIn = new Date();
          checkIn.setHours(inHour, inMinute, inSecond);
          
          const checkOut = new Date();
          checkOut.setHours(outHour, outMinute, outSecond);
          
          const diffMs = checkOut - checkIn;
          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
          const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          
          return `${diffHours}.${diffMinutes.toString().padStart(2, '0')} hrs`;
        };

        const formattedData = result.data.items.map(item => ({
          id: item.id,
          empId: item.empId,
          employeeUserId: item.employeeUserId,
          academyid: item.academyId?.toString(),
          academyName: item.academyName,
          employeeName: item.name,
          employeePhone: item.mobileNumber,
          checkinDate: new Date(item.checkInDate).toLocaleDateString(),
          checkinTime: formatTime(item.checkInTime),
          checkoutTime: formatTime(item.checkOutTime),
          workingHours: calculateWorkingHours(item.checkInTime, item.checkOutTime),
          department: item.departmentName || 'N/A',
          departmentId: item.departmentId,
          role: item.roleName || 'N/A',
          roleId: item.roleId,
          centerName: item.academyName,
          reason: item.reason || '-',
          status: item.status ? "Success" : "Failure",
          checkInDate: item.checkInDate,
          checkInTime: item.checkInTime,
          checkOutTime: item.checkOutTime,
          isEnabled: item.isEnabled
        }));

        setAttendanceData(formattedData);
        setAcademiesLoaded(true);
      } else {
        setAttendanceData([]);
        setDepartmentOptions([]);
        setRoleOptions([]);
        setEmployeeOptions([]);
        setAcademiesLoaded(true);
      }
    } catch (e) {
      console.error('Error fetching attendance data:', e);
      setError('Failed to fetch attendance data.');
      setAttendanceData([]);
      setDepartmentOptions([]);
      setRoleOptions([]);
      setEmployeeOptions([]);
      setAcademiesLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // Update role options when department changes
  useEffect(() => {
    if (selectedDepartment && attendanceData.length > 0) {
      const rolesInDepartment = new Set();
      
      attendanceData.forEach(item => {
        if (item.department === selectedDepartment.value && item.role) {
          rolesInDepartment.add(item.role);
        }
      });

      const filteredRoleOptions = Array.from(rolesInDepartment).map(role => ({
        value: role,
        label: role
      }));

      setRoleOptions(filteredRoleOptions);
      setSelectedRole(null);
    } else if (!selectedDepartment && attendanceData.length > 0) {
      const allRoles = new Set();
      attendanceData.forEach(item => {
        if (item.role) {
          allRoles.add(item.role);
        }
      });

      const allRoleOptions = Array.from(allRoles).map(role => ({
        value: role,
        label: role
      }));

      setRoleOptions(allRoleOptions);
    }
  }, [selectedDepartment, attendanceData]);

  // Update employee options when filters change
  useEffect(() => {
    if (attendanceData.length > 0) {
      const filteredEmployees = new Set();
      
      attendanceData.forEach(item => {
        const departmentMatch = !selectedDepartment || item.department === selectedDepartment.value;
        const roleMatch = !selectedRole || item.role === selectedRole.value;
        const academyMatch = !selectedAcademy || String(item.academyid) === String(selectedAcademy?.value);

        if (departmentMatch && roleMatch && academyMatch && item.employeeName && item.empId) {
          filteredEmployees.add(JSON.stringify({
            value: item.empId.toString(),
            label: item.employeeName,
            academy: item.academyid,
            contact: item.employeePhone,
            department: item.department,
            role: item.role
          }));
        }
      });

      const employeeOptions = Array.from(filteredEmployees).map(emp => JSON.parse(emp));
      setEmployeeOptions(employeeOptions);
      
      if (selectedEmployee && !employeeOptions.find(emp => emp.value === selectedEmployee.value)) {
        setSelectedEmployee(null);
      }
    }
  }, [selectedDepartment, selectedRole, selectedAcademy, attendanceData]);

  // Initial data fetch
  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (academiesLoaded) {
      fetchAllData();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (academyOptions.length > 0 && selectedCenter) {
      const match = academyOptions.find(a => a.value === String(selectedCenter));
      if (match && (!selectedAcademy || selectedAcademy.value !== match.value)) {
        setSelectedAcademy(match);
      }
    }
  }, [academyOptions, selectedCenter]);

  const handleItemsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };

  const filteredData = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    return attendanceData.filter((item) => {
      const searchMatch =
        !term ||
        (item.employeeName && item.employeeName.toLowerCase().includes(term)) ||
        (item.employeePhone || '').includes(searchTerm) ||
        (item.centerName && item.centerName.toLowerCase().includes(term)) ||
        (item.department && item.department.toLowerCase().includes(term)) ||
        (item.role && item.role.toLowerCase().includes(term));

      const departmentMatch = !selectedDepartment || item.department === selectedDepartment.value;
      const roleMatch = !selectedRole || item.role === selectedRole.value;
      const employeeMatch = !selectedEmployee || item.employeeName === selectedEmployee.label;
      const centerMatch = !selectedAcademy || String(item.academyid) === String(selectedAcademy.value);
      const statusMatch = !selectedStatus || item.status === selectedStatus.value;

      return searchMatch && departmentMatch && roleMatch && employeeMatch && centerMatch && statusMatch;
    });
  }, [attendanceData, searchTerm, selectedDepartment, selectedRole, selectedEmployee, selectedAcademy, selectedStatus]);

  const [sortConfig, setSortConfig] = useState({ key: 'checkinDate', direction: 'ascending' });
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  };

  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig;
    return [...filteredData].sort((a, b) => {
      if (key === 'checkinDate') {
        const dateA = new Date(a.checkInDate);
        const dateB = new Date(b.checkInDate);
        return direction === 'ascending' ? dateA - dateB : dateB - dateA;
      }
      
      if (key === 'checkinTime' || key === 'checkoutTime') {
        const timeA = a[key.replace('checkin', 'checkIn').replace('checkout', 'checkOut')] || '';
        const timeB = b[key.replace('checkin', 'checkIn').replace('checkout', 'checkOut')] || '';
        return direction === 'ascending' ? timeA.localeCompare(timeB) : timeB.localeCompare(timeA);
      }
      
      const x = a[key] ?? '';
      const y = b[key] ?? '';
      if (x < y) return direction === 'ascending' ? -1 : 1;
      if (x > y) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const renderPagination = () => {
    const pages = [];
    const totalPagesToShow = 5;
    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages.map((p, idx) =>
      typeof p === 'number' ? (
        <button key={idx} className={`page-number ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>
          {p}
        </button>
      ) : (
        <span key={idx} className="pagination-dots">{p}</span>
      )
    );
  };

  const totalEntries = filteredData.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  return (
    <div className="enrollment-container">
      {/* Centered Filter Section */}
      <div className="filter-section-center">
        <div className="sales-filters-login">
          {/* Center */}
          <div className="floating-select">
            <Select
              className={`sales-filter-court-booking ${selectedAcademy ? 'has-value' : ''}`}
              classNamePrefix="react-select"
              options={academyOptions}
              value={selectedAcademy}
              onChange={(opt) => { 
                setSelectedAcademy(opt || null); 
                setSelectedEmployee(null);
                setSelectedDepartment(null);
                setSelectedRole(null);
                setCurrentPage(1); 
              }}
              placeholder="Select Center"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{
                placeholder: (p) => ({ ...p, color: 'black' }),
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
              }}
              isClearable
            />
            {selectedAcademy && <label htmlFor="selectedAcademy">Center</label>}
          </div>

          {/* Department */}
          <div className="floating-select">
            <Select
              className={`sales-filter-court-booking ${selectedDepartment ? 'has-value' : ''}`}
              classNamePrefix="react-select"
              options={departmentOptions}
              value={selectedDepartment}
              onChange={(opt) => { setSelectedDepartment(opt); setCurrentPage(1); }}
              placeholder="Select Department"
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{
                placeholder: (p) => ({ ...p, color: 'black' }),
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
              }}
            />
            {selectedDepartment && <label htmlFor="selectedDepartment">Department</label>}
          </div>

          {/* Role */}
          <div className="floating-select">
            <Select
              className={`sales-filter-court-booking ${selectedRole ? 'has-value' : ''}`}
              classNamePrefix="react-select"
              options={roleOptions}
              value={selectedRole}
              onChange={(opt) => { setSelectedRole(opt); setCurrentPage(1); }}
              placeholder="Select Role"
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{
                placeholder: (p) => ({ ...p, color: 'black' }),
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
              }}
            />
            {selectedRole && <label htmlFor="selectedRole">Role</label>}
          </div>

          {/* Employee */}
          <div className="floating-select">
            <Select
              className={`sales-filter-court-booking ${selectedEmployee ? 'has-value' : ''}`}
              classNamePrefix="react-select"
              options={employeeOptions}
              value={selectedEmployee}
              onChange={(opt) => { setSelectedEmployee(opt); setCurrentPage(1); }}
              placeholder="Select Employee"
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{
                placeholder: (p) => ({ ...p, color: 'black' }),
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
              }}
            />
            {selectedEmployee && <label htmlFor="selectedEmployee">Employee</label>}
          </div>

          {/* Status */}
          <div className="floating-select">
            <Select
              className={`sales-filter-court-booking ${selectedStatus ? 'has-value' : ''}`}
              classNamePrefix="react-select"
              options={statusOptions}
              value={selectedStatus}
              onChange={(opt) => { setSelectedStatus(opt); setCurrentPage(1); }}
              placeholder="Select Status"
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{
                placeholder: (p) => ({ ...p, color: 'black' }),
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
              }}
            />
            {selectedStatus && <label htmlFor="selectedStatus">Status</label>}
          </div>

          {/* Dates */}
          <div className="floating-select">
            <input
              type="date"
              id="startDate"
              className={`sales-filter-academy ${startDate ? 'has-value' : ''}`}
              value={startDate || ''}
              onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
              onClick={(e) => e.target.showPicker()}
              max={new Date().toISOString().split('T')[0]}
            />
            {startDate && <label htmlFor="startDate">Start Date</label>}
          </div>
          <div className="floating-select">
            <input
              type="date"
              id="endDate"
              className={`sales-filter-academy ${endDate ? 'has-value' : ''}`}
              value={endDate || ''}
              min={startDate || ''}
              onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
              onClick={(e) => e.target.showPicker()}
              max={new Date().toISOString().split('T')[0]}
            />
            {endDate && <label htmlFor="endDate">End Date</label>}
          </div>
        </div>
      </div>

      {/* Search + Export */}
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? 'has-value' : ''}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">🔍 Search</label>
        </div>

        <CSVLink
          data={filteredData}
          headers={headers}
          filename="Employee_Attendance.csv"
          className="btn btn-primary no-decoration"
        >
          <button className="csv_button" type="button">
            <span className="button__text">Export CSV</span>
            <span className="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,1,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
              </svg>
            </span>
          </button>
        </CSVLink>
      </div>

      {/* Controls */}
      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchAllData} disabled={loading}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track"><div className="progress-bar"></div></div>
          <div className="status-text">
            <span>L</span><span>O</span><span>A</span><span>D</span><span>I</span><span>N</span><span>G</span>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredData.length === 0 && (
        <div className="error-message">No attendance data available</div>
      )}

      {/* Table */}
      <div className="tt-table-container">
        <table className="tt-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.direction === 'descending' && sortConfig.key === key ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? currentRows.map((item, idx) => (
              <tr key={idx}>
                <td>{item.centerName}</td>
                <td>{item.employeeName}</td>
                <td>{item.department}</td>
                <td>{item.role}</td>
                <td>{item.employeePhone}</td>
                <td>{item.checkinDate}</td>
                <td>{item.checkinTime}</td>
                <td>{item.checkoutTime}</td>
                <td>{item.workingHours}</td>
                <td>{item.reason}</td>
                <td>
                  <span className={`status-badge ${item.status === 'Success' ? 'status-success' : 'status-failure'}`}>
                    {item.status}
                  </span>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={headers.length} style={{ textAlign: 'center' }}>No data available</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button className="prev-button" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
        {renderPagination()}
        <button className="next-button" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default EmployeeAttendance;